import { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ThemeProvider } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../../../../context/UserContext";
import { hasRole } from "../../../../lib/auth";
import { usePopupMenu } from "../../../primitives/navigation/PopupMenu";
import { Pill } from "../../../primitives/data-display/Pill";
import { Spacer } from "../../../primitives/layout/Spacer";
import { theme as publicTheme } from "../../../../theme";
import { ROUTES, signInPath } from "../../../../route_paths";

const environment = import.meta.env.VITE_ENVIRONMENT as string | undefined;
const showEnv = Boolean(environment) && environment !== "production";

interface NavBarProps {
  /** When true, applies frosted-glass elevation on scroll and keeps the bar sticky. */
  frosted?: boolean;
}

interface FrostedAppBarProps {
  $frosted: boolean;
  $scrolled: boolean;
}

const FrostedAppBar = styled(AppBar)<FrostedAppBarProps>(({ theme, $frosted, $scrolled }) => ({
  padding: 0,
  ...($frosted
    ? {
        backgroundColor: $scrolled ? "rgba(244, 241, 234, 0.72)" : "transparent",
        backdropFilter: $scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: $scrolled ? "blur(12px)" : "none",
        borderBottom: `1px solid ${$scrolled ? theme.palette.divider : "transparent"}`,
        transition: "background-color 0.25s ease, border-color 0.25s ease",
      }
    : {
        backgroundColor: "transparent",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
}));

const GappedToolbar = styled(Toolbar)({ gap: 8 });

const BrandLink = styled(Typography)({
  textDecoration: "none",
  marginRight: 8,
}) as typeof Typography;

const VersionStack = styled(Stack)({ userSelect: "none", pointerEvents: "none" });

const EnvLabel = styled("span")(({ theme }) => ({
  fontSize: "0.6rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.warning.main,
}));

const NavAvatar = styled(Avatar)({ width: 28, height: 28, fontSize: 13 });

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

/** Unified top navigation bar. Always renders under the public theme for visual consistency across sections. */
export function NavBar({ frosted }: NavBarProps) {
  const { t } = useTranslation();
  const { session, profile } = useUserContext();
  const navigate = useNavigate();
  const scrolled = useScrolled();
  const isMobile = useMediaQuery(publicTheme.breakpoints.down("sm"));

  const user = session?.user ?? null;
  const isNamed = !!user && !user.is_anonymous;

  const showExpertInsights = isNamed && hasRole(user, "expert_insights.expert");
  const showKodakku = isNamed && hasRole(user, "corpus.annotator");
  const hasRoleLinks = showExpertInsights || showKodakku;

  function openSignIn() {
    navigate(signInPath());
  }

  // Mobile nav popup: role links + Verify Claim (when there are role links to switch between).
  const mobileNavItems = [
    ...(showExpertInsights
      ? [
          {
            label: t("nav.expertInsights"),
            onClick: () => navigate(ROUTES.expertInsights.insights.path()),
          },
        ]
      : []),
    ...(showKodakku
      ? [{ label: t("nav.kodakku"), onClick: () => navigate(ROUTES.internal.home.path()) }]
      : []),
    ...(hasRoleLinks
      ? [{ label: t("header.verifyClaim"), onClick: () => navigate(ROUTES.landing.path()) }]
      : []),
  ];
  const { menu: mobileNavMenu, setPopupMenuAnchor: setMobileNavAnchor } = usePopupMenu(
    mobileNavItems.length > 0 ? [mobileNavItems] : []
  );

  return (
    <ThemeProvider theme={publicTheme}>
      <FrostedAppBar
        position={frosted ? "sticky" : "static"}
        elevation={0}
        color="default"
        $frosted={!!frosted}
        $scrolled={scrolled}
      >
        <GappedToolbar>
          {/* Mobile nav hamburger — left of brand, only when there are role links to show */}
          {isMobile && mobileNavItems.length > 0 && (
            <IconButton size="small" onClick={setMobileNavAnchor} aria-label={t("nav.menu")}>
              <MenuIcon />
            </IconButton>
          )}
          {mobileNavMenu}

          {/* Brand */}
          <BrandLink
            variant="h6"
            fontWeight={600}
            color="primary"
            component={Link}
            to={ROUTES.landing.path()}
          >
            {t("header.brand")}
          </BrandLink>

          {/* Role-based section links — desktop only */}
          {!isMobile && showExpertInsights && (
            <Button
              component={Link}
              to={ROUTES.expertInsights.insights.path()}
              color="inherit"
              size="small"
            >
              {t("nav.expertInsights")}
            </Button>
          )}
          {!isMobile && showKodakku && (
            <Button component={Link} to={ROUTES.internal.home.path()} color="inherit" size="small">
              {t("nav.kodakku")}
            </Button>
          )}

          <Spacer />

          {/* Verify CTA — desktop only, shown when the user has other sections */}
          {!isMobile && hasRoleLinks && (
            <Button color="inherit" size="small" component={Link} to={ROUTES.landing.path()}>
              {t("header.verifyClaim")}
            </Button>
          )}

          {/* Release stage badge — non-interactive */}
          <VersionStack direction="row" gap={0.5} alignItems="center">
            {showEnv && <EnvLabel>{environment}</EnvLabel>}
            <Pill tone="warning" label={t("header.releaseStage")} />
          </VersionStack>

          {/* Auth slot */}
          {!isNamed ? (
            <Button variant="outlined" size="small" onClick={openSignIn}>
              {t("nav.signIn")}
            </Button>
          ) : (
            <Tooltip title={user.email ?? ""}>
              <IconButton
                size="small"
                onClick={() => navigate(ROUTES.account.path())}
                aria-label={t("nav.account")}
              >
                <NavAvatar src={profile?.avatarUrl ?? undefined}>
                  {user.email?.[0]?.toUpperCase() ?? "?"}
                </NavAvatar>
              </IconButton>
            </Tooltip>
          )}
        </GappedToolbar>
      </FrostedAppBar>
    </ThemeProvider>
  );
}
